
"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { app as firebaseApp } from '@/lib/firebase';
import { fetchAndActivate, getValue, isSupported as isRemoteConfigSupported, getRemoteConfig } from 'firebase/remote-config';
import { DEFAULT_RAZORPAY_INTEGRATION_ENABLED, DEFAULT_MAINTENANCE_MODE_ACTIVE } from '@/lib/constants';

interface RemoteConfigValues {
  razorpayIntegrationEnabled: boolean;
  maintenanceModeActive: boolean;
  razorpayMode: 'test' | 'live';
}

interface RemoteConfigContextType extends RemoteConfigValues {
  loadingConfig: boolean;
  errorConfig: Error | null;
  refreshConfig: () => Promise<void>;
}

const defaultValues: RemoteConfigValues = {
  razorpayIntegrationEnabled: DEFAULT_RAZORPAY_INTEGRATION_ENABLED,
  maintenanceModeActive: DEFAULT_MAINTENANCE_MODE_ACTIVE,
  razorpayMode: 'live', // Default to live mode
};

const RemoteConfigContext = createContext<RemoteConfigContextType | undefined>(undefined);

export const RemoteConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [configValues, setConfigValues] = useState<RemoteConfigValues>(defaultValues);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [errorConfig, setErrorConfig] = useState<Error | null>(null);

  const fetchRemoteConfigValues = useCallback(async () => {
    setLoadingConfig(true);
    setErrorConfig(null);

    try {
      const supported = await isRemoteConfigSupported();
      if (supported && firebaseApp && typeof firebaseApp.name === 'string' && !firebaseApp.name.startsWith("dummy-app-")) {
        const currentRemoteConfigInstance = getRemoteConfig(firebaseApp);
        
        if (currentRemoteConfigInstance && currentRemoteConfigInstance.settings) {
          currentRemoteConfigInstance.defaultConfig = {
            'razorpay_integration_enabled': defaultValues.razorpayIntegrationEnabled,
            'maintenance_mode_active': defaultValues.maintenanceModeActive,
            'razorpay_mode': defaultValues.razorpayMode, 
          };
          
          const fetchIntervalMillis = process.env.NODE_ENV === 'development' ? 10000 : 3600000;
          if (currentRemoteConfigInstance.settings.minimumFetchIntervalMillis !== fetchIntervalMillis) {
               currentRemoteConfigInstance.settings.minimumFetchIntervalMillis = fetchIntervalMillis;
          }

          await fetchAndActivate(currentRemoteConfigInstance);

          const newValues: Partial<RemoteConfigValues> = {};
          newValues.razorpayIntegrationEnabled = getValue(currentRemoteConfigInstance, 'razorpay_integration_enabled').asBoolean();
          newValues.maintenanceModeActive = getValue(currentRemoteConfigInstance, 'maintenance_mode_active').asBoolean();
          const fetchedRazorpayMode = getValue(currentRemoteConfigInstance, 'razorpay_mode').asString();
          newValues.razorpayMode = fetchedRazorpayMode === 'live' ? 'live' : 'test'; // Ensure valid value, client still defaults to 'test' if value is garbage
          
          setConfigValues(prev => ({ ...prev, ...newValues }));
        } else {
          console.warn("RemoteConfigContext: Firebase Remote Config instance is not valid. Using default config values.");
          setConfigValues(defaultValues);
        }
      } else {
        if (!supported) {
          console.warn("RemoteConfigContext: Firebase Remote Config is not supported in this environment. Using default config values.");
        } else {
          console.warn("RemoteConfigContext: Firebase App instance is not valid. Using default config values.");
        }
        setConfigValues(defaultValues);
      }
    } catch (err) {
      console.error("RemoteConfigContext: Error fetching or activating Remote Config:", err);
      setErrorConfig(err instanceof Error ? err : new Error(String(err)));
      setConfigValues(defaultValues);
    } finally {
      setLoadingConfig(false);
    }
  }, []); 

  useEffect(() => {
    fetchRemoteConfigValues();
  }, [fetchRemoteConfigValues]);

  const contextValue = useMemo(() => ({
    ...configValues,
    loadingConfig,
    errorConfig,
    refreshConfig: fetchRemoteConfigValues,
  }), [configValues, loadingConfig, errorConfig, fetchRemoteConfigValues]);

  return (
    <RemoteConfigContext.Provider value={contextValue}>
      {children}
    </RemoteConfigContext.Provider>
  );
};

export const useRemoteConfig = (): RemoteConfigContextType => {
  const context = useContext(RemoteConfigContext);
  if (context === undefined) {
    throw new Error('useRemoteConfig must be used within a RemoteConfigProvider');
  }
  return context;
};
